import * as Y from "yjs";
import { FILE_TEXT_FIELD, TREE_NODES_FIELD } from "./docs";

/**
 * Árvore de arquivos do projeto, guardada no documento `tree:<projectId>`.
 *
 * Cada nó aponta para o pai (`parentId`). Renomear ou mover uma pasta é uma
 * única escrita, e o caminho completo é derivado. Edições concorrentes no mesmo
 * nó seguem "a última escrita vence", o que é aceitável para metadados.
 */
export type NodeType = "file" | "folder";

export interface TreeNode {
  id: string;
  name: string;
  parentId: string | null;
  type: NodeType;
  createdAt: number;
  /**
   * Arquivo binário ou grande (banco .db, imagem, zip, log de vários MB): o conteúdo fica no
   * armazenamento de arquivos do projeto (pelo sha256), não no documento de texto.
   */
  blob?: BlobRef;
}

export interface BlobRef {
  sha256: string;
  size: number;
}

/** Arquivo guardado como binário (sem texto editável). */
export function isBlobNode(node: TreeNode | undefined | null): node is TreeNode & { blob: BlobRef } {
  return Boolean(node && node.type === "file" && node.blob && /^[0-9a-f]{64}$/.test(node.blob.sha256));
}

export const MAX_NAME_LENGTH = 128;
export const MAX_DEPTH = 32;
export const MAX_NODES = 5_000;

// Proíbe separadores, caracteres de controle e caracteres inválidos no Windows.
// eslint-disable-next-line no-control-regex
const FORBIDDEN_CHARS = /[\\/:*?"<>|\u0000-\u001f]/;

/** Valida o nome de um arquivo/pasta. Devolve a mensagem de erro ou null. */
export function validateNodeName(raw: string): string | null {
  const name = raw.trim();
  if (!name) return "O nome não pode ser vazio.";
  if (name.length > MAX_NAME_LENGTH) return `Use no máximo ${MAX_NAME_LENGTH} caracteres.`;
  if (name === "." || name === "..") return "Nome reservado.";
  if (FORBIDDEN_CHARS.test(name)) return 'Não use / \\ : * ? " < > | nem caracteres de controle.';
  return null;
}

export function nodesOf(doc: Y.Doc): Y.Map<TreeNode> {
  return doc.getMap<TreeNode>(TREE_NODES_FIELD);
}

/** Lê o mapa Yjs como objetos simples, descartando entradas malformadas. */
export function readNodes(map: Y.Map<TreeNode>): Map<string, TreeNode> {
  const out = new Map<string, TreeNode>();
  map.forEach((value, key) => {
    if (isTreeNode(value) && value.id === key) out.set(key, value);
  });
  return out;
}

function isTreeNode(v: unknown): v is TreeNode {
  if (!v || typeof v !== "object") return false;
  const n = v as Record<string, unknown>;
  return (
    typeof n.id === "string" &&
    typeof n.name === "string" &&
    (n.parentId === null || typeof n.parentId === "string") &&
    (n.type === "file" || n.type === "folder") &&
    typeof n.createdAt === "number" &&
    (n.blob === undefined ||
      (!!n.blob &&
        typeof (n.blob as BlobRef).sha256 === "string" &&
        typeof (n.blob as BlobRef).size === "number"))
  );
}

export interface ResolvedTree {
  /** Caminho relativo (com "/") de cada nó alcançável a partir da raiz. */
  paths: Map<string, string>;
  /** Filhos por pai (null = raiz), ordenados: pastas primeiro, depois por nome. */
  children: Map<string | null, TreeNode[]>;
}

/**
 * Resolve caminhos de forma defensiva: o conteúdo da árvore vem de clientes e
 * pode ter ciclos, pais inexistentes ou nomes repetidos (criação concorrente).
 * Nós inalcançáveis são ignorados; nomes repetidos ganham um sufixo estável.
 */
export function resolveTree(nodes: Map<string, TreeNode>): ResolvedTree {
  const byParent = new Map<string | null, TreeNode[]>();
  for (const node of nodes.values()) {
    if (validateNodeName(node.name) !== null) continue;
    const parent =
      node.parentId !== null && nodes.has(node.parentId)
        ? node.parentId
        : node.parentId === null
          ? null
          : undefined;
    if (parent === undefined) continue; // pai inexistente: órfão
    const list = byParent.get(parent) ?? [];
    list.push(node);
    byParent.set(parent, list);
  }

  const paths = new Map<string, string>();
  const children = new Map<string | null, TreeNode[]>();
  const visit = (parentId: string | null, prefix: string, depth: number) => {
    if (depth > MAX_DEPTH) return;
    const list = (byParent.get(parentId) ?? []).slice().sort(compareNodes);
    const used = new Set<string>();
    const resolved: TreeNode[] = [];
    // Ordem estável para desempatar nomes repetidos: o mais antigo fica com o nome.
    const byAge = list.slice().sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
    const names = new Map<string, string>();
    for (const node of byAge) {
      let name = node.name.trim();
      if (used.has(name)) name = dedupeName(name, node.id);
      used.add(name);
      names.set(node.id, name);
    }
    for (const node of list) {
      if (paths.has(node.id)) continue; // ciclo
      const name = names.get(node.id)!;
      const path = prefix ? `${prefix}/${name}` : name;
      paths.set(node.id, path);
      resolved.push(name === node.name ? node : { ...node, name });
      if (node.type === "folder") visit(node.id, path, depth + 1);
    }
    children.set(parentId, resolved);
  };
  visit(null, "", 0);
  return { paths, children };
}

function dedupeName(name: string, id: string) {
  const dot = name.lastIndexOf(".");
  const suffix = `~${id.slice(0, 4)}`;
  return dot > 0 ? `${name.slice(0, dot)}${suffix}${name.slice(dot)}` : `${name}${suffix}`;
}

export function compareNodes(a: TreeNode, b: TreeNode) {
  if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base", numeric: true });
}

/** true se `nodeId` é `ancestorId` ou está dentro dele (evita mover uma pasta para dentro de si). */
export function isSameOrDescendant(nodes: Map<string, TreeNode>, nodeId: string, ancestorId: string) {
  let current: string | null = nodeId;
  for (let i = 0; current && i <= MAX_DEPTH + 1; i++) {
    if (current === ancestorId) return true;
    current = nodes.get(current)?.parentId ?? null;
  }
  return false;
}

/** Ids de um nó e de todos os seus descendentes. */
export function collectSubtree(nodes: Map<string, TreeNode>, rootId: string): string[] {
  const out: string[] = [];
  const stack = [rootId];
  const seen = new Set<string>();
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    for (const node of nodes.values()) if (node.parentId === id) stack.push(node.id);
  }
  return out;
}

/** Cria o conteúdo inicial de um documento de arquivo. */
export function encodeFileDoc(content: string): Uint8Array {
  const doc = new Y.Doc();
  doc.getText(FILE_TEXT_FIELD).insert(0, content);
  const update = Y.encodeStateAsUpdate(doc);
  doc.destroy();
  return update;
}

/** Lê o texto de um snapshot de arquivo. */
export function decodeFileDoc(state: Uint8Array): string {
  const doc = new Y.Doc();
  Y.applyUpdate(doc, state);
  const text = doc.getText(FILE_TEXT_FIELD).toString();
  doc.destroy();
  return text;
}
